/**
 * @fileoverview Facebook OAuth API service.
 * @module features/settings/api/facebookOAuthService
 */

import { z } from 'zod'
import apiClient from '@/lib/apiClient'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'

/**
 * Schema for OAuth configuration response.
 */
export const OAuthConfigSchema = z.object({
  appId: z.string(),
  configId: z.string(),
  redirectUri: z.string(),
  connectEnabled: z.boolean(),
})

export type OAuthConfig = z.infer<typeof OAuthConfigSchema>

/**
 * Schema for ad account info.
 */
export const AdAccountInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  currency: z.string().nullable(),
  businessName: z.string().nullable(),
})

export type AdAccountInfo = z.infer<typeof AdAccountInfoSchema>

/**
 * Schema for callback response.
 */
export const CallbackResponseSchema = z.object({
  success: z.boolean(),
  adAccounts: z.array(AdAccountInfoSchema),
  selectedAdAccountId: z.string().nullable(),
  message: z.string(),
})

export type CallbackResponse = z.infer<typeof CallbackResponseSchema>

/**
 * Fetches Facebook OAuth configuration.
 *
 * @param orgId Organization UUID
 * @returns OAuth configuration for initiating login flow
 */
export async function getFacebookOAuthConfig(
  orgId: z.infer<typeof OrgIdSchema>
): Promise<OAuthConfig> {
  const id = OrgIdSchema.parse(orgId)
  const res = await apiClient.get(`/orgs/${id}/integrations/facebook/oauth/config`)
  return OAuthConfigSchema.parse(res.data)
}

/**
 * Exchanges authorization code for access token and fetches ad accounts.
 *
 * @param orgId Organization UUID
 * @param code Authorization code from Facebook
 * @returns Callback response with ad accounts
 */
export async function exchangeFacebookAuthCode(
  orgId: z.infer<typeof OrgIdSchema>,
  code: string,
  forceBackfill = false
): Promise<CallbackResponse> {
  const id = OrgIdSchema.parse(orgId)
  const res = await apiClient.post(`/orgs/${id}/integrations/facebook/oauth/callback`, {
    code,
    forceBackfill,
  })
  return CallbackResponseSchema.parse(res.data)
}

/**
 * Completes OAuth flow with selected ad account.
 *
 * @param orgId Organization UUID
 * @param code Authorization code from Facebook
 * @param adAccountId Selected ad account ID
 */
export async function completeFacebookOAuth(
  orgId: z.infer<typeof OrgIdSchema>,
  code: string,
  adAccountId: string,
  forceBackfill = false
): Promise<void> {
  const id = OrgIdSchema.parse(orgId)
  await apiClient.post(`/orgs/${id}/integrations/facebook/oauth/complete`, {
    code,
    adAccountId,
    forceBackfill,
  })
}

/**
 * Disconnects Facebook integration by revoking credentials.
 *
 * @param orgId Organization UUID
 */
export async function disconnectFacebook(
  orgId: z.infer<typeof OrgIdSchema>
): Promise<void> {
  const id = OrgIdSchema.parse(orgId)
  await apiClient.delete(`/orgs/${id}/integrations/facebook/oauth`)
}
